import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft, ChevronRight as ChevronRightIcon, Box, CheckCircle2, Circle, Pencil, Layers, Link2, Trash2, Plus, X, Globe } from 'lucide-react'
import {
  getCup, getCupTraits, getCupRelations, getCupChildRelations, getCoffeesPaginated, getCups,
  updateCup, deleteCup, deleteCoffee, getBeans, addBean, updateBean, removeBean,
  addCupTrait, updateCupTrait, removeCupTrait, addCupRelation, updateCupRelation, removeCupRelation,
  getTraits, getIngredients, updateTrait, getCollections, createRecord,
  getCupGlobalRelations, addCupGlobalRelation, updateCupGlobalRelation, removeCupGlobalRelation,
} from '../api/brainer'
import { Copy, Check } from 'lucide-react'
import { Modal } from '../components/Modal'
import { CupRelationsGraph } from '../components/SchemaGraph'
import { BeanInput } from '../components/BeanInput'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { InterviewerWizard } from '../components/InterviewerWizard'
import { GLOBAL_CLASS_TITLES, GLOBAL_RELATION_TYPE_TITLES } from '../api/global-options'
import { useDateFmt } from '../hooks/useDateFmt'
import type { BCup, BCupTrait, BCoffee, BBean, BCupRelation, BCupRelationType, BFlavor, BTrait, BIngredient, BCollection, BJourneyInterviewerForm, BCoffeeValidationError, BCupGlobalRelation, BGlobalClass, BGlobalRelationType, BGlobalTarget } from '../types'

const PAGE_SIZE_OPTIONS = [25, 50, 100]

// ── Relations modal ───────────────────────────────────────────────────────────

function RelationsModal({ cup, onClose }: { cup: BCup; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: cups = [] } = useQuery({ queryKey: ['b-cups'], queryFn: () => getCups().then(r => r.data) })
  const { data: hasManyRels = [] } = useQuery({
    queryKey: ['b-cup-relations', cup.id],
    queryFn: () => getCupRelations(cup.id).then(r => r.data),
  })
  const { data: belongsToRels = [] } = useQuery({
    queryKey: ['b-cup-child-relations', cup.id],
    queryFn: () => getCupChildRelations(cup.id).then(r => r.data),
  })

  const [direction, setDirection] = useState<'belongs-to' | 'has-many'>('belongs-to')
  const [addForm, setAddForm] = useState({ otherCupId: '', label: '', relationType: 'belongs_to' as BCupRelationType, mandatory: false })
  const otherCups = (cups as BCup[]).filter(c => c.id !== cup.id)

  const cupById = useMemo(() => {
    const m = new Map<string, BCup>()
    for (const c of cups as BCup[]) m.set(c.id, c)
    return m
  }, [cups])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['b-cup-relations', cup.id] })
    qc.invalidateQueries({ queryKey: ['b-cup-child-relations', cup.id] })
  }

  const addRel = useMutation({
    mutationFn: () => direction === 'belongs-to'
      ? addCupRelation(addForm.otherCupId, { childCupId: cup.id, label: addForm.label || undefined, relationType: addForm.relationType, mandatory: addForm.mandatory })
      : addCupRelation(cup.id, { childCupId: addForm.otherCupId, label: addForm.label || undefined, relationType: addForm.relationType, mandatory: addForm.mandatory }),
    onSuccess: () => { invalidate(); setAddForm({ otherCupId: '', label: '', relationType: 'belongs_to', mandatory: false }) },
  })
  const removeRel = useMutation({
    mutationFn: (id: string) => removeCupRelation(id),
    onSuccess: invalidate,
  })

  const REL_TYPE_LABELS: Record<BCupRelationType, string> = { belongs_to: 'belongs to', owns: 'owns', many_to_many: 'M2M' }
  const REL_TYPE_COLORS: Record<BCupRelationType, string> = {
    belongs_to: 'bg-amber-50 text-amber-600 border-amber-100',
    owns: 'bg-blue-50 text-blue-600 border-blue-100',
    many_to_many: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  }

  const RelRow = ({ rel, nameA, nameB }: { rel: BCupRelation; nameA: string; nameB: string }) => {
    const [editing, setEditing] = useState(false)
    const [labelDraft, setLabelDraft] = useState(rel.label ?? '')
    const [relTypeDraft, setRelTypeDraft] = useState<BCupRelationType>(rel.relationType ?? 'belongs_to')
    const [mandatoryDraft, setMandatoryDraft] = useState(rel.mandatory ?? false)
    const updateRel = useMutation({
      mutationFn: () => updateCupRelation(rel.id, { label: labelDraft || null, relationType: relTypeDraft, mandatory: mandatoryDraft }),
      onSuccess: () => { invalidate(); setEditing(false) },
    })
    function startEdit() { setLabelDraft(rel.label ?? ''); setRelTypeDraft(rel.relationType ?? 'belongs_to'); setMandatoryDraft(rel.mandatory ?? false); setEditing(true) }
    return (
      <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-100">
        <div className="flex items-center gap-1.5 text-sm min-w-0 flex-1">
          <span className="font-medium text-neutral-800 shrink-0">{nameA}</span>
          {editing ? (
            <>
              <input
                autoFocus
                value={labelDraft}
                onChange={e => setLabelDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') updateRel.mutate(); if (e.key === 'Escape') setEditing(false) }}
                placeholder="label"
                className="field !py-0.5 !text-theme-xs w-24 mx-1"
              />
              <select
                value={relTypeDraft}
                onChange={e => setRelTypeDraft(e.target.value as BCupRelationType)}
                className="field !py-0.5 !text-theme-xs w-28"
              >
                <option value="belongs_to">belongs to</option>
                <option value="owns">owns</option>
                <option value="many_to_many">many to many</option>
              </select>
              <label className="flex items-center gap-1 text-theme-xs text-neutral-600 cursor-pointer ml-1">
                <input type="checkbox" checked={mandatoryDraft} onChange={e => setMandatoryDraft(e.target.checked)} className="rounded" />
                mandatory
              </label>
            </>
          ) : (
            <>
              <button onClick={startEdit} className="mx-1 text-neutral-400 hover:text-neutral-700 transition-colors">
                <span>{rel.label || '→'}</span>
              </button>
              <span className={`text-[10px] font-medium px-1.5 py-px rounded border ${REL_TYPE_COLORS[rel.relationType ?? 'belongs_to']}`}>
                {REL_TYPE_LABELS[rel.relationType ?? 'belongs_to']}
              </span>
              {rel.mandatory && <span className="text-[10px] font-medium text-red-500 ml-1">*</span>}
            </>
          )}
          <span className="font-medium text-neutral-800 shrink-0 ml-1">{nameB}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {editing ? (
            <>
              <button onClick={() => updateRel.mutate()} disabled={updateRel.isPending} className="text-blue-500 hover:text-blue-700 transition-colors"><Check size={13} /></button>
              <button onClick={() => setEditing(false)} className="text-neutral-300 hover:text-neutral-600 transition-colors"><X size={13} /></button>
            </>
          ) : (
            <button onClick={startEdit} className="text-neutral-300 hover:text-neutral-600 transition-colors"><Pencil size={12} /></button>
          )}
          <button onClick={() => removeRel.mutate(rel.id)} className="text-neutral-300 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
        </div>
      </div>
    )
  }

  return (
    <Modal title={`Relations — ${cup.title}`} onClose={onClose} size="lg">
      <div className="space-y-5">
        {/* Direction toggle + add form */}
        <div className="space-y-3">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {([['belongs-to', `${cup.title} belongs to…`], ['has-many', `${cup.title} has many…`]] as const).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setDirection(val)}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  direction === val ? 'bg-white shadow-sm text-neutral-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Field label={direction === 'belongs-to' ? 'Parent cup' : 'Child cup'}>
                <select value={addForm.otherCupId} onChange={e => setAddForm(f => ({ ...f, otherCupId: e.target.value }))} className="field">
                  <option value="">Select cup</option>
                  {otherCups.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex-1">
              <Field label="Label">
                <input value={addForm.label} onChange={e => setAddForm(f => ({ ...f, label: e.target.value }))} placeholder="optional" className="field" />
              </Field>
            </div>
            <div className="flex-1">
              <Field label="Type">
                <select value={addForm.relationType} onChange={e => setAddForm(f => ({ ...f, relationType: e.target.value as BCupRelationType }))} className="field">
                  <option value="belongs_to">belongs to</option>
                  <option value="owns">owns</option>
                  <option value="many_to_many">many to many</option>
                </select>
              </Field>
            </div>
            <div className="flex flex-col justify-end mb-0.5">
              <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer whitespace-nowrap h-9">
                <input type="checkbox" checked={addForm.mandatory} onChange={e => setAddForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded" />
                Mandatory
              </label>
            </div>
            <Button onClick={() => addRel.mutate()} disabled={!addForm.otherCupId || addRel.isPending} className="mb-0.5" icon={Plus} />
          </div>
        </div>

        {/* Belongs-to list */}
        {(belongsToRels as BCupRelation[]).length > 0 && (
          <div>
            <p className="text-theme-xs font-semibold text-amber-600 uppercase tracking-widest mb-2">Belongs to</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(belongsToRels as BCupRelation[]).map(rel => (
                <RelRow key={rel.id} rel={rel}
                  nameA={cup.title}
                  nameB={cupById.get(rel.parentCupId)?.title ?? rel.parentCupId.slice(0, 8)} />
              ))}
            </div>
          </div>
        )}

        {/* Has-many list */}
        {(hasManyRels as BCupRelation[]).length > 0 && (
          <div>
            <p className="text-theme-xs font-semibold text-emerald-600 uppercase tracking-widest mb-2">Has many</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(hasManyRels as BCupRelation[]).map(rel => (
                <RelRow key={rel.id} rel={rel}
                  nameA={cup.title}
                  nameB={cupById.get(rel.childCupId)?.title ?? rel.childCupId.slice(0, 8)} />
              ))}
            </div>
          </div>
        )}

        {(belongsToRels as BCupRelation[]).length === 0 && (hasManyRels as BCupRelation[]).length === 0 && (
          <p className="text-sm text-neutral-400 text-center py-4">No relations defined</p>
        )}
      </div>
    </Modal>
  )
}

// ── Edit traits modal ─────────────────────────────────────────────────────────

interface TraitRow {
  cupTraitId: string
  traitId: string
  title: string
  ingredientId: string
  collectionId: string
  position: string
  mandatory: boolean
  uniqueField: boolean
  isIdentifier: boolean
}

function EditTraitsModal({ cup, onClose }: { cup: BCup; onClose: () => void }) {
  const qc = useQueryClient()

  const { data: cupTraitsData = [], isLoading: traitsLoading } = useQuery({
    queryKey: ['b-cup-traits', cup.id],
    queryFn: () => getCupTraits(cup.id).then(r => r.data),
  })
  const { data: allTraitsData = [], isLoading: allTraitsLoading } = useQuery({
    queryKey: ['b-traits'],
    queryFn: () => getTraits().then(r => r.data),
  })
  const { data: ingredientsData = [], isLoading: ingredientsLoading } = useQuery({
    queryKey: ['b-ingredients'],
    queryFn: () => getIngredients().then(r => r.data),
  })
  const { data: collectionsData = [], isLoading: collectionsLoading } = useQuery({
    queryKey: ['b-collections'],
    queryFn: () => getCollections().then(r => r.data),
  })

  const loading = traitsLoading || allTraitsLoading || ingredientsLoading || collectionsLoading

  const [rows, setRows] = useState<TraitRow[]>([])
  const [initialized, setInitialized] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!loading && !initialized) {
      setRows(
        (cupTraitsData as BCupTrait[])
          .slice()
          .sort((a, b) => a.position - b.position)
          .map(ct => ({
            cupTraitId: ct.id,
            traitId: ct.traitId,
            title: ct.trait?.title ?? '',
            ingredientId: ct.trait?.ingredientId ?? '',
            collectionId: ct.trait?.collectionId ?? '',
            position: String(ct.position),
            mandatory: ct.mandatory,
            uniqueField: ct.uniqueField,
            isIdentifier: ct.isIdentifier,
          }))
      )
      setInitialized(true)
    }
  }, [loading, initialized, cupTraitsData])

  const [addForm, setAddForm] = useState({ traitId: '', position: '0', mandatory: true, uniqueField: false, isIdentifier: false })

  const availableTraits = (allTraitsData as BTrait[]).filter(
    t => !(cupTraitsData as BCupTrait[]).some(ct => ct.traitId === t.id)
  )

  const addTrait = useMutation({
    mutationFn: () => addCupTrait(cup.id, {
      traitId: addForm.traitId,
      position: +addForm.position,
      mandatory: addForm.mandatory,
      uniqueField: addForm.uniqueField,
      isIdentifier: addForm.isIdentifier,
    }),
    onSuccess: (res) => {
      const ct = res.data
      const trait = (allTraitsData as BTrait[]).find(t => t.id === ct.traitId)
      setRows(prev => {
        const updated = ct.isIdentifier ? prev.map(r => ({ ...r, isIdentifier: false })) : prev
        return [...updated, {
          cupTraitId: ct.id,
          traitId: ct.traitId,
          title: trait?.title ?? '',
          ingredientId: trait?.ingredientId ?? '',
          collectionId: trait?.collectionId ?? '',
          position: String(ct.position),
          mandatory: ct.mandatory,
          uniqueField: ct.uniqueField,
          isIdentifier: ct.isIdentifier,
        }]
      })
      qc.invalidateQueries({ queryKey: ['b-cup-traits', cup.id] })
      setAddForm({ traitId: '', position: '0', mandatory: true, uniqueField: false, isIdentifier: false })
    },
  })

  const removeTrait = useMutation({
    mutationFn: (cupTraitId: string) => removeCupTrait(cupTraitId),
    onSuccess: (_, cupTraitId) => {
      setRows(prev => prev.filter(r => r.cupTraitId !== cupTraitId))
      qc.invalidateQueries({ queryKey: ['b-cup-traits', cup.id] })
    },
  })

  function updateRow(cupTraitId: string, field: keyof TraitRow, value: string | boolean) {
    setRows(prev => prev.map(r => {
      if (r.cupTraitId === cupTraitId) return { ...r, [field]: value }
      // isIdentifier is exclusive — clear others when one is set
      if (field === 'isIdentifier' && value === true) return { ...r, isIdentifier: false }
      return r
    }))
  }

  async function handleSave() {
    setSaving(true)
    try {
      for (const row of rows) {
        const orig = (cupTraitsData as BCupTrait[]).find(ct => ct.id === row.cupTraitId)
        if (!orig) continue
        const posChanged = +row.position !== orig.position
        const flagsChanged = row.mandatory !== orig.mandatory || row.uniqueField !== orig.uniqueField || row.isIdentifier !== orig.isIdentifier
        if (posChanged || flagsChanged) {
          await updateCupTrait(row.cupTraitId, { position: +row.position, mandatory: row.mandatory, uniqueField: row.uniqueField, isIdentifier: row.isIdentifier })
        }
        const titleChanged = row.title !== (orig.trait?.title ?? '')
        const typeChanged = row.ingredientId !== (orig.trait?.ingredientId ?? '')
        const collectionChanged = row.collectionId !== (orig.trait?.collectionId ?? '')
        if (titleChanged || typeChanged || collectionChanged) {
          await updateTrait(row.traitId, {
            title: row.title,
            ingredientId: row.ingredientId || undefined,
            collectionId: row.collectionId || undefined,
          })
        }
      }
      qc.invalidateQueries({ queryKey: ['b-cup-traits', cup.id] })
      qc.invalidateQueries({ queryKey: ['b-traits'] })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={`Edit traits — ${cup.title}`} onClose={onClose} size="xl">
      {loading || !initialized ? (
        <div className="py-8 text-center text-sm text-neutral-400">Loading…</div>
      ) : (
        <>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_1fr_5rem_5.5rem_4.5rem_4.5rem_2rem] gap-2 px-2 pb-2 border-b border-slate-100">
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest">Name</span>
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest">Type</span>
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest">Position</span>
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest text-center">Mandatory</span>
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest text-center">Unique</span>
            <span className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest text-center">Identifier</span>
            <span />
          </div>

          {/* Trait rows */}
          <div className="space-y-1 max-h-[50vh] overflow-y-auto py-1">
            {rows.length === 0 ? (
              <p className="text-sm text-neutral-400 text-center py-6">No traits yet — add one below.</p>
            ) : rows.map(row => {
              const ing = (ingredientsData as BIngredient[]).find(i => i.id === row.ingredientId)
              const isArray = ing?.name?.toLowerCase() === 'array'
              return (
                <div key={row.cupTraitId} className="px-2 py-1.5 rounded hover:bg-slate-50">
                  <div className="grid grid-cols-[1fr_1fr_5rem_5.5rem_4.5rem_4.5rem_2rem] gap-2 items-center">
                    <input
                      value={row.title}
                      onChange={e => updateRow(row.cupTraitId, 'title', e.target.value)}
                      className="field text-xs"
                    />
                    <select
                      value={row.ingredientId}
                      onChange={e => updateRow(row.cupTraitId, 'ingredientId', e.target.value)}
                      className="field text-xs"
                    >
                      <option value="">— type —</option>
                      {(ingredientsData as BIngredient[]).map(ing => (
                        <option key={ing.id} value={ing.id}>{ing.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={row.position}
                      onChange={e => updateRow(row.cupTraitId, 'position', e.target.value)}
                      min="0"
                      className="field text-xs"
                    />
                    <div className="flex justify-center">
                      <input type="checkbox" checked={row.mandatory}
                        onChange={e => updateRow(row.cupTraitId, 'mandatory', e.target.checked)}
                        className="rounded" />
                    </div>
                    <div className="flex justify-center">
                      <input type="checkbox" checked={row.uniqueField}
                        onChange={e => updateRow(row.cupTraitId, 'uniqueField', e.target.checked)}
                        className="rounded" />
                    </div>
                    <div className="flex justify-center">
                      <input type="checkbox" checked={row.isIdentifier}
                        onChange={e => updateRow(row.cupTraitId, 'isIdentifier', e.target.checked)}
                        className="rounded accent-amber-500" />
                    </div>
                    <button onClick={() => removeTrait.mutate(row.cupTraitId)}
                      className="text-neutral-300 hover:text-red-500 transition-colors flex justify-center"
                      disabled={removeTrait.isPending}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {isArray && (
                    <div className="mt-1.5 flex items-center gap-2 pl-1">
                      <span className="text-theme-xs text-neutral-400 shrink-0">Collection</span>
                      <select
                        value={row.collectionId}
                        onChange={e => updateRow(row.cupTraitId, 'collectionId', e.target.value)}
                        className="field text-xs flex-1"
                      >
                        <option value="">— select collection —</option>
                        {(collectionsData as BCollection[]).map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Add trait */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">Add trait</p>
            <div className="flex gap-2 items-center">
              <select value={addForm.traitId} onChange={e => setAddForm(f => ({ ...f, traitId: e.target.value }))} className="field flex-1">
                <option value="">Select trait</option>
                {availableTraits.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
              <input type="number" value={addForm.position} onChange={e => setAddForm(f => ({ ...f, position: e.target.value }))}
                min="0" placeholder="Pos" className="field w-16" />
              <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={addForm.mandatory} onChange={e => setAddForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded" />
                Mandatory
              </label>
              <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={addForm.uniqueField} onChange={e => setAddForm(f => ({ ...f, uniqueField: e.target.checked }))} className="rounded" />
                Unique
              </label>
              <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={addForm.isIdentifier} onChange={e => setAddForm(f => ({ ...f, isIdentifier: e.target.checked }))} className="rounded accent-amber-500" />
                Identifier
              </label>
              <Button variant="secondary" onClick={() => addTrait.mutate()} disabled={!addForm.traitId || addTrait.isPending} icon={Plus}>
                Add
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ cup }: { cup: BCup }) {
  const fmt = useDateFmt()
  const [traitFilter, setTraitFilter] = useState('')

  const { data: traits = [] } = useQuery({
    queryKey: ['b-cup-traits', cup.id],
    queryFn: () => getCupTraits(cup.id).then(r => r.data),
  })

  const { data: hasManyRels = [] } = useQuery({
    queryKey: ['b-cup-relations', cup.id],
    queryFn: () => getCupRelations(cup.id).then(r => r.data),
  })

  const { data: belongsToRels = [] } = useQuery({
    queryKey: ['b-cup-child-relations', cup.id],
    queryFn: () => getCupChildRelations(cup.id).then(r => r.data),
  })

  const { data: allCups = [] } = useQuery({
    queryKey: ['b-cups'],
    queryFn: () => getCups().then(r => r.data),
  })

  const filteredTraits = useMemo(() => {
    const q = traitFilter.toLowerCase()
    return (traits as BCupTrait[]).filter(t =>
      !q || t.trait?.title.toLowerCase().includes(q)
    ).sort((a, b) => a.position - b.position)
  }, [traits, traitFilter])

  const cupById = useMemo(() => {
    const m = new Map<string, BCup>()
    for (const c of allCups as BCup[]) m.set(c.id, c)
    return m
  }, [allCups])

  return (
    <div className="flex gap-6">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Description */}
        <div className="mb-5">
          <div className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest mb-1.5">Description</div>
          {cup.description ? (
            <p className="text-sm text-neutral-600 leading-relaxed">{cup.description}</p>
          ) : (
            <p className="text-sm text-neutral-300 italic">No description</p>
          )}
        </div>

        {/* Traits table */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest">
              Traits ({(traits as BCupTrait[]).length})
            </div>
            <input
              value={traitFilter}
              onChange={e => setTraitFilter(e.target.value)}
              placeholder="Filter traits…"
              className="ml-auto h-7 px-2.5 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 w-44"
            />
          </div>

          {filteredTraits.length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-lg py-8 text-center text-sm text-neutral-400">
              {(traits as BCupTrait[]).length === 0 ? 'No traits configured for this cup.' : 'No traits match filter.'}
            </div>
          ) : (
            <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Trait</th>
                  <th>Type</th>
                  <th>Comment</th>
                  <th className="text-center">Mandatory</th>
                  <th className="text-center">Unique</th>
                  <th className="text-center">Identifier</th>
                  <th className="text-right">Position</th>
                </tr>
              </thead>
              <tbody>
                {filteredTraits.map(ct => (
                  <tr key={ct.id}>
                    <td className="font-medium">{ct.trait?.title ?? ct.traitId}</td>
                    <td>
                      <span className="text-theme-xs font-mono bg-slate-100 text-neutral-500 px-1.5 py-0.5 rounded">
                        {ct.trait?.ingredient?.name ?? '—'}
                      </span>
                    </td>
                    <td className="text-neutral-400 text-xs">{ct.trait?.description ?? ''}</td>
                    <td className="text-center">
                      {ct.mandatory
                        ? <CheckCircle2 size={13} className="text-emerald-500 mx-auto" />
                        : <Circle size={13} className="text-slate-200 mx-auto" />}
                    </td>
                    <td className="text-center">
                      {ct.uniqueField
                        ? <CheckCircle2 size={13} className="text-blue-500 mx-auto" />
                        : <Circle size={13} className="text-slate-200 mx-auto" />}
                    </td>
                    <td className="text-center">
                      {ct.isIdentifier
                        ? <CheckCircle2 size={13} className="text-amber-500 mx-auto" />
                        : <Circle size={13} className="text-slate-200 mx-auto" />}
                    </td>
                    <td className="text-right text-neutral-400 font-mono text-xs">{ct.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Right sidebar */}
      <div className="w-60 shrink-0 space-y-5">
        <div className="border border-slate-100 rounded-lg p-4 text-sm space-y-3">
          <div className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest">About this cup</div>
          <div>
            <span className="text-neutral-400 text-xs">ID</span>
            <div className="font-mono text-theme-xs text-neutral-600 truncate mt-0.5">{cup.id}</div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-400 text-xs">Disposable</span>
            <span className={`text-theme-xs font-medium px-1.5 py-px rounded border ${
              cup.disposable
                ? 'bg-amber-50 text-amber-600 border-amber-100'
                : 'bg-slate-50 text-neutral-500 border-slate-200'
            }`}>{cup.disposable ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-400 text-xs">Traits</span>
            <span className="text-neutral-600 text-xs font-medium">{(traits as BCupTrait[]).length}</span>
          </div>
          <div>
            <span className="text-neutral-400 text-xs">Created</span>
            <div className="text-neutral-600 text-xs mt-0.5">{fmt(cup.createdAt)}</div>
          </div>
          <div>
            <span className="text-neutral-400 text-xs">Updated</span>
            <div className="text-neutral-600 text-xs mt-0.5">{fmt(cup.updatedAt)}</div>
          </div>
        </div>

        {((belongsToRels as BCupRelation[]).length > 0 || (hasManyRels as BCupRelation[]).length > 0) && (
          <div className="border border-slate-100 rounded-lg p-4 text-sm space-y-3">
            {(belongsToRels as BCupRelation[]).length > 0 && (
              <div>
                <div className="text-theme-xs font-semibold text-amber-500 uppercase tracking-widest mb-2">Belongs to</div>
                <div className="space-y-1.5">
                  {(belongsToRels as BCupRelation[]).map(r => {
                    const parent = cupById.get(r.parentCupId)
                    return (
                      <div key={r.id} className="flex items-center gap-1.5 text-xs text-neutral-600">
                        <ChevronRightIcon size={11} className="text-amber-300 shrink-0" />
                        <span className="font-medium">{parent?.title ?? r.parentCupId}</span>
                        {r.label && <span className="text-neutral-400">({r.label})</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {(hasManyRels as BCupRelation[]).length > 0 && (
              <div>
                <div className="text-theme-xs font-semibold text-emerald-600 uppercase tracking-widest mb-2">Has many</div>
                <div className="space-y-1.5">
                  {(hasManyRels as BCupRelation[]).map(r => {
                    const child = cupById.get(r.childCupId)
                    return (
                      <div key={r.id} className="flex items-center gap-1.5 text-xs text-neutral-600">
                        <ChevronRightIcon size={11} className="text-emerald-300 shrink-0" />
                        <span className="font-medium">{child?.title ?? r.childCupId}</span>
                        {r.label && <span className="text-neutral-400">({r.label})</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Coffee edit modal ─────────────────────────────────────────────────────────

function CoffeeEditModal({ coffee, traits, onClose }: {
  coffee: BCoffee
  traits: BCupTrait[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { data: beans = [], isLoading } = useQuery({
    queryKey: ['b-beans', coffee.id],
    queryFn: () => getBeans(coffee.id).then(r => r.data),
  })

  const [values, setValues] = useState<Record<string, string>>({})
  const [initialized, setInitialized] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isLoading && !initialized) {
      const init: Record<string, string> = {}
      for (const ct of traits) {
        const bean = (beans as BBean[]).find(b => b.cupTraitId === ct.id)
        init[ct.id] = bean?.chunk ?? ''
      }
      setValues(init)
      setInitialized(true)
    }
  }, [isLoading, initialized, beans, traits])

  async function handleSave() {
    setSaving(true)
    try {
      for (const ct of traits) {
        const bean = (beans as BBean[]).find(b => b.cupTraitId === ct.id)
        const val = values[ct.id] ?? ''
        if (val) {
          if (bean) await updateBean(bean.id, { chunk: val })
          else await addBean(coffee.id, { cupTraitId: ct.id, chunk: val })
        } else if (bean) {
          await removeBean(bean.id)
        }
      }
      qc.invalidateQueries({ queryKey: ['b-beans', coffee.id] })
      qc.invalidateQueries({ queryKey: ['b-cup-coffees', coffee.cupId] })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Edit record" onClose={onClose} size="md">
      {isLoading || !initialized ? (
        <div className="py-8 text-center text-sm text-neutral-400">Loading…</div>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {traits.map(ct => (
            <Field key={ct.id} label={`${ct.trait?.title ?? ct.traitId}${ct.mandatory ? ' *' : ''}`} description={ct.trait?.description}>
              <BeanInput
                ingredientName={ct.trait?.ingredient?.name}
                value={values[ct.id] ?? ''}
                onChange={v => setValues(prev => ({ ...prev, [ct.id]: v }))}
              />
            </Field>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || !initialized}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  )
}

// ── Coffee detail offcanvas ───────────────────────────────────────────────────

function CoffeeDetailOffcanvas({ coffee, traits, belongsToRelations, cupById, onClose, onEdit }: {
  coffee: BCoffee
  traits: BCupTrait[]
  belongsToRelations: BCupRelation[]
  cupById: Map<string, BCup>
  onClose: () => void
  onEdit: () => void
}) {
  const fmt = useDateFmt()

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-[420px] bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-paper-border">
          <div className="min-w-0">
            <p className="text-theme-xs font-mono text-neutral-400 truncate">{coffee.id}</p>
            <p className="text-xs text-neutral-400 mt-0.5">
              created {fmt(coffee.createdAt)}
              {coffee.updatedAt && coffee.updatedAt !== coffee.createdAt && (
                <span className="ml-2">· updated {fmt(coffee.updatedAt)}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {traits.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-3">Fields</h3>
              <div className="space-y-3">
                {traits.map(ct => {
                  const bean = (coffee.beans ?? []).find((b: BBean) => b.cupTraitId === ct.id)
                  return (
                    <div key={ct.id}>
                      <div className="text-theme-xs text-neutral-400 mb-0.5">
                        {ct.trait?.title ?? ct.traitId}
                        {ct.trait?.ingredient?.name && (
                          <span className="ml-1 font-mono text-neutral-300 text-[9px]">{ct.trait.ingredient.name}</span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-800 break-words">
                        {bean?.chunk
                          ? bean.chunk
                          : <span className="text-neutral-300 italic text-xs">null</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {belongsToRelations.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-3">Relations</h3>
              <div className="space-y-3">
                {belongsToRelations.map(rel => {
                  const flavor = (coffee.flavors ?? []).find((f: BFlavor) => f.cupRelationId === rel.id)
                  const parentCupTitle = cupById.get(rel.parentCupId)?.title ?? rel.parentCupId.slice(0, 8)
                  return (
                    <div key={rel.id}>
                      <div className="text-theme-xs text-neutral-400 mb-0.5">
                        {parentCupTitle}
                        <span className="ml-1 font-mono text-amber-300 text-[9px]">belongs to</span>
                      </div>
                      <div className="text-sm font-mono text-amber-700 break-all">
                        {flavor?.parentCoffeeId
                          ? flavor.parentCoffeeId
                          : <span className="text-neutral-300 italic text-xs not-italic font-sans">—</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  )
}

// ── Data tab ──────────────────────────────────────────────────────────────────

function DataTab({ cup }: { cup: BCup }) {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const [page, setPage]       = useState(1)
  const [limit, setLimit]     = useState(50)
  const [rowFilter, setRowFilter] = useState('')
  const [viewCoffee, setViewCoffee] = useState<BCoffee | null>(null)
  const [editCoffee, setEditCoffee] = useState<BCoffee | null>(null)
  const [adding, setAdding] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<BCoffee | null>(null)
  const delCoffee = useMutation({
    mutationFn: (id: string) => deleteCoffee(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-cup-coffees', cup.id] }); setDeleteTarget(null) },
  })

  const { data: traitsData = [] } = useQuery({
    queryKey: ['b-cup-traits', cup.id],
    queryFn: () => getCupTraits(cup.id).then(r => r.data),
  })
  const traits = useMemo(
    () => (traitsData as BCupTrait[]).slice().sort((a, b) => a.position - b.position),
    [traitsData],
  )

  const { data: childRelationsData = [] } = useQuery({
    queryKey: ['b-cup-child-relations', cup.id],
    queryFn: () => getCupChildRelations(cup.id).then(r => r.data),
  })
  const belongsToRelations = childRelationsData as BCupRelation[]

  const { data: allCupsData = [] } = useQuery({
    queryKey: ['b-cups'],
    queryFn: () => getCups().then(r => r.data),
    enabled: belongsToRelations.length > 0,
  })
  const cupById = useMemo(() => {
    const m = new Map<string, BCup>()
    for (const c of allCupsData as BCup[]) m.set(c.id, c)
    return m
  }, [allCupsData])

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['b-cup-coffees', cup.id, page, limit],
    queryFn: () => getCoffeesPaginated(cup.id, page, limit).then(r => r.data),
    placeholderData: prev => prev,
  })

  const coffees: BCoffee[] = pageData?.rows ?? []
  const total: number      = pageData?.total ?? 0
  const totalPages         = Math.max(1, Math.ceil(total / limit))
  const from               = total === 0 ? 0 : (page - 1) * limit + 1
  const to                 = Math.min(page * limit, total)

  // Client-side filter on coffee id (fast, no server round-trip for small searches)
  const visibleRows = useMemo(() => {
    if (!rowFilter) return coffees
    const q = rowFilter.toLowerCase()
    return coffees.filter(c => c.id.toLowerCase().includes(q))
  }, [coffees, rowFilter])

  function beanValue(coffee: BCoffee, cupTraitId: string): string {
    const bean = (coffee.beans ?? []).find((b: BBean) => b.cupTraitId === cupTraitId)
    return bean?.chunk ?? ''
  }

  function flavorValue(coffee: BCoffee, relationId: string): string {
    const flavor = (coffee.flavors ?? []).find((f: BFlavor) => f.cupRelationId === relationId)
    return flavor?.parentCoffeeId ?? ''
  }

  function goToPage(p: number) {
    setPage(Math.max(1, Math.min(totalPages, p)))
  }

  function changeLimit(newLimit: number) {
    setLimit(newLimit)
    setPage(1)
  }

  // Visible page numbers: always show first, last, current ±1, with ellipsis
  const pageNumbers = useMemo(() => {
    const pages: (number | '…')[] = []
    const add = new Set<number>()
    ;[1, page - 1, page, page + 1, totalPages].forEach(p => {
      if (p >= 1 && p <= totalPages) add.add(p)
    })
    const sorted = [...add].sort((a, b) => a - b)
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push('…')
      pages.push(sorted[i])
    }
    return pages
  }, [page, totalPages])

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <input
          value={rowFilter}
          onChange={e => setRowFilter(e.target.value)}
          placeholder="Filter by ID…"
          className="h-7 px-2.5 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 w-52"
        />
        {rowFilter && (
          <button onClick={() => setRowFilter('')} className="text-xs text-neutral-400 hover:text-neutral-600">
            Reset
          </button>
        )}
        {isFetching && (
          <span className="text-theme-xs text-neutral-400 ml-auto">Loading…</span>
        )}
        <Button size="sm" icon={Plus} onClick={() => setAdding(true)} disabled={traits.length === 0} className={isFetching ? '' : 'ml-auto'}>
          Add record
        </Button>
      </div>

      {/* Table */}
      {traits.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg py-12 text-center text-sm text-neutral-400">
          No traits configured — add traits to this cup to see data columns.
        </div>
      ) : total === 0 && !isFetching ? (
        <div className="border border-dashed border-slate-200 rounded-lg py-12 text-center text-sm text-neutral-400">
          No records in this cup yet.
        </div>
      ) : (
        <div className="table-wrap overflow-x-auto">
          <table className="data-table min-w-max">
            <thead>
              <tr>
                <th className="text-right text-xs w-10">#</th>
                <th className="text-xs">id</th>
                {traits.map(ct => (
                  <th key={ct.id} className="text-xs">
                    {ct.trait?.title ?? ct.traitId}
                    <span className="ml-1 text-[9px] font-normal text-neutral-300 font-mono">
                      {ct.trait?.ingredient?.name ?? ''}
                    </span>
                  </th>
                ))}
                {belongsToRelations.map(rel => (
                  <th key={rel.id} className="text-xs text-amber-600">
                    {cupById.get(rel.parentCupId)?.title ?? rel.parentCupId.slice(0, 8)}
                    <span className="ml-1 text-[9px] font-normal text-amber-300">belongs to</span>
                  </th>
                ))}
                <th className="text-xs">created_at</th>
                <th className="text-xs">updated_at</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((coffee, idx) => (
                <tr
                  key={coffee.id}
                  className="cursor-pointer"
                  onClick={() => setViewCoffee(coffee)}
                >
                  <td className="text-right text-neutral-300 text-xs font-mono">
                    {from + idx}
                  </td>
                  <td className="font-mono text-theme-xs text-neutral-400">
                    {coffee.id}
                  </td>
                  {traits.map(ct => {
                    const val = beanValue(coffee, ct.id)
                    return (
                      <td key={ct.id} className="max-w-[200px]">
                        {val
                          ? <span className="truncate block" title={val}>{val}</span>
                          : <span className="text-neutral-300 text-theme-xs italic">null</span>}
                      </td>
                    )
                  })}
                  {belongsToRelations.map(rel => {
                    const parentId = flavorValue(coffee, rel.id)
                    return (
                      <td key={rel.id} className="max-w-[180px]">
                        {parentId
                          ? <span className="font-mono text-theme-xs text-amber-700 truncate block" title={parentId}>{parentId.slice(0, 8)}…</span>
                          : <span className="text-neutral-300 text-theme-xs italic">—</span>}
                      </td>
                    )
                  })}
                  <td className="text-neutral-400 text-xs">{fmt(coffee.createdAt)}</td>
                  <td className="text-neutral-400 text-xs">{fmt(coffee.updatedAt)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={e => { e.stopPropagation(); setEditCoffee(coffee) }}
                        className="text-neutral-300 hover:text-neutral-700 transition-colors"
                        title="Edit record"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteTarget(coffee) }}
                        className="text-neutral-300 hover:text-red-600 transition-colors"
                        title="Delete record"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete coffee"
          message="Delete this record? All beans and flavors will be removed."
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => delCoffee.mutate(deleteTarget.id)}
          loading={delCoffee.isPending}
        />
      )}

      {/* Pagination bar */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span>Showing {from}–{to} of {total.toLocaleString()} records</span>
            <span className="text-neutral-200">·</span>
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={e => changeLimit(Number(e.target.value))}
              className="h-6 px-1.5 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className="flex items-center gap-0.5 px-2 h-7 text-xs text-neutral-600 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={12} /> Prev
            </button>

            {pageNumbers.map((p, i) =>
              p === '…' ? (
                <span key={`ellipsis-${i}`} className="px-1 text-neutral-300 text-xs">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  className={`w-7 h-7 text-xs rounded border transition-colors ${
                    p === page
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'text-neutral-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              className="flex items-center gap-0.5 px-2 h-7 text-xs text-neutral-600 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRightIcon size={12} />
            </button>
          </div>
        </div>
      )}

      {viewCoffee && !editCoffee && (
        <CoffeeDetailOffcanvas
          coffee={viewCoffee}
          traits={traits}
          belongsToRelations={belongsToRelations}
          cupById={cupById}
          onClose={() => setViewCoffee(null)}
          onEdit={() => { setEditCoffee(viewCoffee); setViewCoffee(null) }}
        />
      )}

      {adding && (
        <NewRecordModal
          cup={cup}
          traits={traits}
          relations={belongsToRelations.filter(r => r.childCupId === cup.id && r.relationType !== 'owns')}
          cupById={cupById}
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['b-cup-coffees', cup.id] }) }}
        />
      )}
      {editCoffee && (
        <CoffeeEditModal coffee={editCoffee} traits={traits} onClose={() => setEditCoffee(null)} />
      )}
    </div>
  )
}

// ── Info tab ──────────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button onClick={handleCopy} className="ml-2 text-slate-400 hover:text-slate-600 transition-colors shrink-0" title="Copy">
      {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
    </button>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  const text = JSON.stringify(value, null, 2)
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-200 rounded-lg p-4 text-theme-xs font-mono leading-relaxed overflow-x-auto max-h-96 scrollbar-thin">
        {text}
      </pre>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <CopyButton text={text} />
      </div>
    </div>
  )
}

function ApiEndpoint({
  method, path, description, params, example,
}: {
  method: string
  path: string
  description: string
  params?: { name: string; type: string; required: boolean; description: string }[]
  example: unknown
}) {
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-start gap-3">
        <span className="mt-px px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 shrink-0">{method}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <code className="text-sm font-mono text-slate-700 break-all">{path}</code>
            <CopyButton text={path} />
          </div>
          <p className="text-xs text-slate-400 mt-1">{description}</p>
        </div>
      </div>

      {params && params.length > 0 && (
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Parameters</p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text-xs">Name</th>
                  <th className="text-xs">Type</th>
                  <th className="text-xs">Required</th>
                  <th className="text-xs">Description</th>
                </tr>
              </thead>
              <tbody>
                {params.map(p => (
                  <tr key={p.name}>
                    <td className="font-mono">{p.name}</td>
                    <td className="text-slate-400">{p.type}</td>
                    <td>
                      {p.required
                        ? <span className="text-red-500 font-medium">yes</span>
                        : <span className="text-slate-300">no</span>}
                    </td>
                    <td className="text-slate-500">{p.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Example response</p>
        <JsonBlock value={example} />
      </div>
    </div>
  )
}

function InfoTab({ cup }: { cup: BCup }) {
  const { data: traitsData = [] } = useQuery({
    queryKey: ['b-cup-traits', cup.id],
    queryFn: () => getCupTraits(cup.id).then(r => r.data),
  })
  const traits = (traitsData as BCupTrait[]).slice().sort((a, b) => a.position - b.position)

  const { data: relationsData = [] } = useQuery({
    queryKey: ['b-cup-relations', cup.id],
    queryFn: () => getCupRelations(cup.id).then(r => r.data),
  })
  const hasManyRelations = relationsData as BCupRelation[]

  const { data: childRelationsData = [] } = useQuery({
    queryKey: ['b-cup-child-relations', cup.id],
    queryFn: () => getCupChildRelations(cup.id).then(r => r.data),
  })
  const belongsToRelations = childRelationsData as BCupRelation[]

  const { data: globalRelations = [] } = useQuery({
    queryKey: ['b-cup-global-relations', cup.id],
    queryFn: () => getCupGlobalRelations(cup.id).then(r => r.data),
  })
  // Global relations come back like parent relations: { id, identifier } for belongs to, a list
  // of them for has many / many to many (identifier = the target's name when it was linked).
  const globalExample = (g: BCupGlobalRelation) => {
    const one = { id: '<uuid>', identifier: `<${GLOBAL_CLASS_TITLES[g.targetClass]} name>` }
    return g.relationType === 'belongs_to' ? one : [one]
  }

  function buildCoffeeExample(recursive = false): Record<string, any> {
    const obj: Record<string, any> = { id: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx' }
    for (const ct of traits) {
      if (ct.trait) obj[ct.trait.title] = `<${ct.trait.ingredient?.name ?? 'string'}>`
    }
    if (!recursive) for (const g of globalRelations) obj[g.label] = globalExample(g)
    if (recursive) {
      for (const rel of belongsToRelations) {
        const label = (rel as any).label ?? rel.id.slice(0, 8)
        obj[label] = { id: '…', '(trait fields)': '…', '(nested relations)': '…' }
      }
      for (const rel of hasManyRelations) {
        const label = (rel as any).label ?? rel.id.slice(0, 8)
        obj[label] = [{ id: '…', '(trait fields)': '…' }]
      }
      if (globalRelations.length) obj.relations = Object.fromEntries(globalRelations.map(g => [g.label, globalExample(g)]))
    }
    return obj
  }

  const listExample = {
    [cup.title]: [buildCoffeeExample(), buildCoffeeExample()],
    total: 2,
    page: 1,
    limit: 25,
  }

  const singleExample = buildCoffeeExample(true)

  const baseUrl = '/api/coffees'

  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-sm text-slate-500">
        These endpoints expose <span className="font-medium text-slate-700">{cup.title}</span> records
        with beans resolved as named fields and relationships resolved recursively.
        All requests require an <code className="text-theme-xs bg-slate-100 px-1 rounded">Authorization</code> header.
      </p>

      <ApiEndpoint
        method="GET"
        path={`${baseUrl}/resolved?cupId=${cup.id}&page=1&limit=25`}
        description={`Returns a paginated list of ${cup.title} records. Each record's beans are resolved as named fields using their trait titles. Belongs-to and has-many relationships are resolved recursively.`}
        params={[
          { name: 'cupId', type: 'uuid', required: true, description: `ID of the ${cup.title} cup` },
          { name: 'page', type: 'number', required: false, description: 'Page number (default: 1)' },
          { name: 'limit', type: 'number', required: false, description: 'Records per page, max 100 (default: 25)' },
        ]}
        example={listExample}
      />

      <ApiEndpoint
        method="GET"
        path={`${baseUrl}/:id/resolved`}
        description={`Returns a single ${cup.title} record by ID with all beans resolved as named fields and all relationships (belongs-to and has-many) resolved recursively down the full graph.`}
        example={singleExample}
      />

      {globalRelations.length > 0 && (
        <>
          <ApiEndpoint
            method="GET"
            path={`${baseUrl}/:id/global-links`}
            description={`Returns a ${cup.title} record's global relations (${globalRelations.map(g => g.label).join(', ')}) with their linked targets.`}
            example={globalRelations.map(g => ({ id: g.id, label: g.label, targetClass: g.targetClass, relationType: g.relationType, mandatory: g.mandatory, targets: [{ id: '<uuid>', label: '<name>' }] }))}
          />
          <ApiEndpoint
            method="PUT"
            path={`${baseUrl}/:id/global-links/:globalRelationId`}
            description="Replaces the record's targets for one global relation. Body: { targets: [{ id, label }] } — at most one target for a belongs-to relation; an empty list unlinks."
            example={{ targets: [{ id: '<uuid>', label: '<name>' }] }}
          />
        </>
      )}
    </div>
  )
}

// ── Schema tab ────────────────────────────────────────────────────────────────

function SchemaTab({ cup }: { cup: BCup }) {
  const { data: childRelations = [] } = useQuery({
    queryKey: ['b-cup-relations', cup.id],
    queryFn: () => getCupRelations(cup.id).then(r => r.data),
  })
  const { data: parentRelations = [] } = useQuery({
    queryKey: ['b-cup-child-relations', cup.id],
    queryFn: () => getCupChildRelations(cup.id).then(r => r.data),
  })
  const { data: allCups = [] } = useQuery({
    queryKey: ['b-cups'],
    queryFn: () => getCups().then(r => r.data),
  })

  return (
    <CupRelationsGraph
      cup={cup}
      parentRelations={parentRelations as BCupRelation[]}
      childRelations={childRelations as BCupRelation[]}
      allCups={allCups as BCup[]}
    />
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'data' | 'schema' | 'info'

export function BrainerCupDetail() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const [tab, setTab]         = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)
  const [showTraits, setShowTraits]     = useState(false)
  const [showRelations, setShowRelations] = useState(false)
  const [showGlobals, setShowGlobals] = useState(false)
  const [showDelete, setShowDelete]     = useState(false)
  const [form, setForm] = useState({ title: '', description: '', disposable: false })

  const { data: cup, isLoading } = useQuery({
    queryKey: ['b-cup', id],
    queryFn:  () => getCup(id!).then(r => r.data),
    enabled:  !!id,
  })

  const editMut = useMutation({
    mutationFn: () => updateCup(id!, { title: form.title, description: form.description || undefined, disposable: form.disposable }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['b-cup', id] })
      qc.invalidateQueries({ queryKey: ['b-cups'] })
      setEditing(false)
    },
  })

  const delMut = useMutation({
    mutationFn: () => deleteCup(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['b-cups'] })
      navigate('/brainer/cups')
    },
  })

  function openEdit() {
    if (!cup) return
    setForm({ title: cup.title, description: cup.description ?? '', disposable: cup.disposable })
    setEditing(true)
  }

  if (isLoading) return <div className="py-16 text-center text-sm text-neutral-400">Loading…</div>
  if (!cup)      return <div className="py-16 text-center text-sm text-red-500">Cup not found</div>

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-3 text-sm">
        <button onClick={() => navigate('/brainer/cups')}
                className="text-neutral-400 hover:text-neutral-700 transition-colors">
          Cups
        </button>
        <ChevronRight size={13} className="text-neutral-300" />
        <span className="text-neutral-700 font-medium">{cup.title}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Box size={16} className="text-neutral-400 shrink-0" />
          <h1 className="text-lg font-semibold text-neutral-900">{cup.title}</h1>
          {cup.disposable && (
            <span className="px-1.5 py-px text-[10px] font-medium rounded border bg-amber-50 text-amber-600 border-amber-100">
              disposable
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setShowTraits(true)} icon={Layers}>
            Edit Traits
          </Button>
          <Button variant="secondary" onClick={() => setShowRelations(true)} icon={Link2}>
            Relations
          </Button>
          <Button variant="secondary" onClick={() => setShowGlobals(true)} icon={Globe}>
            Global relations
          </Button>
          <Button variant="secondary" onClick={openEdit} icon={Pencil}>
            Edit
          </Button>
          <button onClick={() => setShowDelete(true)} className="text-neutral-300 hover:text-red-500 transition-colors p-1.5">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-100 mb-5">
        <nav className="flex -mb-px">
          {(['overview', 'data', 'schema', 'info'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-gray-500 hover:text-neutral-700'
              }`}
            >
              {t === 'overview' ? 'Overview' : t === 'data' ? 'Sample Data' : t === 'schema' ? 'Schema' : 'API'}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {tab === 'overview' ? <OverviewTab cup={cup} /> : tab === 'data' ? <DataTab cup={cup} /> : tab === 'schema' ? <SchemaTab cup={cup} /> : <InfoTab cup={cup} />}

      {/* Edit modal */}
      {editing && (
        <Modal title="Edit cup" onClose={() => setEditing(false)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Cup title"
                className="field"
                autoFocus
              />
            </Field>
            <Field label="Description">
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                className="field"
              />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.disposable}
                onChange={e => setForm(f => ({ ...f, disposable: e.target.checked }))}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-neutral-700">Disposable</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                onClick={() => editMut.mutate()}
                disabled={!form.title || editMut.isPending}
              >
                {editMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showTraits && <EditTraitsModal cup={cup} onClose={() => setShowTraits(false)} />}
      {showRelations && <RelationsModal cup={cup} onClose={() => setShowRelations(false)} />}
      {showGlobals && <GlobalRelationsModal cup={cup} onClose={() => setShowGlobals(false)} />}
      {showDelete && (
        <ConfirmDialog
          title="Delete cup"
          message={`Delete "${cup.title}"? All associated coffees and data will be removed.`}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => delMut.mutate()}
          loading={delMut.isPending}
        />
      )}
    </div>
  )
}

// "Add record" in Sample Data: the cup's own fields and parent links as a single-page form —
// the same form component (and the same rules: required, unique, required relations, read from
// the cup's flags) as every Plate form. Parent links use live search on the parent's identifier.
function NewRecordModal({ cup, traits, relations, cupById, onClose, onCreated }: {
  cup: BCup
  traits: BCupTrait[]
  relations: BCupRelation[]
  cupById: Map<string, BCup>
  onClose: () => void
  onCreated: () => void
}) {
  const [serverErrors, setServerErrors] = useState<BCoffeeValidationError[] | undefined>()
  const [submitError, setSubmitError] = useState('')
  const { data: globalRelations = [], isLoading: globalsLoading } = useQuery({
    queryKey: ['b-cup-global-relations', cup.id],
    queryFn: () => getCupGlobalRelations(cup.id).then(r => r.data),
  })
  const shortTitle = (t?: string) => (t ?? '').startsWith(cup.title + '.') ? t!.slice(cup.title.length + 1) : (t ?? '')

  const form: BJourneyInterviewerForm = useMemo(() => ({
    journeyStepId: '', preparationStepId: '', stepTitle: cup.title, cupId: cup.id, cupTitle: cup.title,
    payloadKey: 'coffeeId', isUpdate: false, layout: 'page',
    parts: [
      {
        id: 'fields', title: 'Fields', position: 0,
        items: traits.map((ct, i) => ({
          id: ct.id, type: 'trait' as const, position: i, cupTraitId: ct.id,
          title: shortTitle(ct.trait?.title) || ct.traitId, description: ct.trait?.description,
          mandatory: ct.mandatory, uniqueField: ct.uniqueField,
          ingredientName: ct.trait?.ingredient?.name,
          collectionItems: (ct.trait?.collection as (BCollection & { items?: { id: string; value: string; position: number }[] }) | undefined)?.items ?? [],
        })),
      },
      ...(relations.length ? [{
        id: 'relations', title: 'Related records', position: 1,
        items: relations.map((r, i) => ({
          id: r.id, type: 'relation' as const, position: i, cupRelationId: r.id, relationType: r.relationType,
          label: [cupById.get(r.parentCupId)?.title, r.label].filter(Boolean).join(' · '),
          parentCupId: r.parentCupId, mandatory: r.mandatory, inputMode: 'live_search' as const,
        })),
      }] : []),
      ...(globalRelations.length ? [{
        id: 'globals', title: 'Linked records', position: 2,
        items: globalRelations.map((g, i) => ({
          id: g.id, type: 'global' as const, position: i, globalRelationId: g.id, targetClass: g.targetClass,
          relationType: g.relationType, label: `${g.label} (${GLOBAL_CLASS_TITLES[g.targetClass]})`, mandatory: g.mandatory, currentValue: [],
        })),
      }] : []),
    ],
  }), [cup, traits, relations, cupById, globalRelations]) // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: (d: { traits: Record<string, string>; relations: Record<string, any>; globals: Record<string, BGlobalTarget[]> }) => createRecord({ cupId: cup.id, ...d }),
    onMutate: () => setSubmitError(''),
    onSuccess: onCreated,
    onError: (err: any) => {
      const data = err?.response?.data
      if (err?.response?.status === 422 && Array.isArray(data?.errors)) setServerErrors([...data.errors])
      else setSubmitError(data?.error ?? err?.message ?? 'Saving failed')
    },
  })

  return (
    <Modal title={`New ${cup.title} record`} onClose={onClose} size="xl">
      <div className="max-h-[75vh] overflow-y-auto pr-1">
        {submitError && <p className="mb-4 rounded-lg border border-error-500 bg-error-50 px-4 py-3 text-sm text-gray-700">Couldn't save — {submitError}</p>}
        {globalsLoading ? <p className="py-6 text-center text-sm text-gray-500">Loading…</p> : <InterviewerWizard
          key={globalRelations.length}
          form={form}
          onSubmit={(traitAnswers, relationAnswers, _chained, globals) => create.mutate({ traits: traitAnswers, relations: relationAnswers, globals })}
          onCancel={onClose}
          isPending={create.isPending}
          serverErrors={serverErrors}
        />}
      </div>
    </Modal>
  )
}

// "Global relations": links from this cup's records to records living in other services — a
// mailbox, users, storage datasets. Forms then let users pick from that service's list (never
// create there). Belongs to = one target per record; has many / many to many = several.
function GlobalRelationsModal({ cup, onClose }: { cup: BCup; onClose: () => void }) {
  const qc = useQueryClient()
  const key = ['b-cup-global-relations', cup.id]
  const { data: relations = [], isLoading } = useQuery({ queryKey: key, queryFn: () => getCupGlobalRelations(cup.id).then(r => r.data) })
  const [form, setForm] = useState<{ label: string; targetClass: BGlobalClass; relationType: BGlobalRelationType; mandatory: boolean }>(
    { label: '', targetClass: 'mailbox', relationType: 'belongs_to', mandatory: false },
  )
  const [error, setError] = useState('')
  const onErr = (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Failed')
  const refresh = () => { setError(''); qc.invalidateQueries({ queryKey: key }) }
  const add = useMutation({
    mutationFn: () => addCupGlobalRelation(cup.id, form),
    onSuccess: () => { refresh(); setForm(f => ({ ...f, label: '', mandatory: false })) },
    onError: onErr,
  })
  const update = useMutation({
    mutationFn: ({ id, d }: { id: string; d: { relationType?: BGlobalRelationType; mandatory?: boolean } }) => updateCupGlobalRelation(id, d),
    onSuccess: refresh, onError: onErr,
  })
  const remove = useMutation({ mutationFn: (id: string) => removeCupGlobalRelation(id), onSuccess: refresh, onError: onErr })
  const classes = Object.keys(GLOBAL_CLASS_TITLES) as BGlobalClass[]
  const types = Object.keys(GLOBAL_RELATION_TYPE_TITLES) as BGlobalRelationType[]

  return (
    <Modal title={`Global relations — ${cup.title}`} onClose={onClose} size="lg">
      <div className="space-y-5">
        <p className="text-sm text-gray-500">
          Link this cup's records to records of other services. In forms, users pick from the existing ones.
          The label is the field's key in the API (e.g. <span className="font-mono">mailbox.identifier</span>).
        </p>
        {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : relations.length === 0 ? (
          <p className="text-sm text-gray-500">No global relations yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Label</th><th>Linked to</th><th>Type</th><th>Required</th><th /></tr></thead>
              <tbody>
                {relations.map((g: BCupGlobalRelation) => (
                  <tr key={g.id}>
                    <td className="font-mono text-sm">{g.label}</td>
                    <td>{GLOBAL_CLASS_TITLES[g.targetClass] ?? g.targetClass}</td>
                    <td>
                      <select value={g.relationType} onChange={e => update.mutate({ id: g.id, d: { relationType: e.target.value as BGlobalRelationType } })} className="field h-9 py-1">
                        {types.map(t => <option key={t} value={t}>{GLOBAL_RELATION_TYPE_TITLES[t]}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="checkbox" checked={g.mandatory} onChange={e => update.mutate({ id: g.id, d: { mandatory: e.target.checked } })} className="rounded w-4 h-4" />
                    </td>
                    <td className="text-right">
                      <button onClick={() => remove.mutate(g.id)} className="text-gray-400 hover:text-error-500" title="Remove (deletes every record's links)"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
          <Field label="Label *">
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="mailbox" className="field font-mono" />
          </Field>
          <Field label="Linked to">
            <select value={form.targetClass} onChange={e => setForm(f => ({ ...f, targetClass: e.target.value as BGlobalClass }))} className="field">
              {classes.map(c => <option key={c} value={c}>{GLOBAL_CLASS_TITLES[c]}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select value={form.relationType} onChange={e => setForm(f => ({ ...f, relationType: e.target.value as BGlobalRelationType }))} className="field">
              {types.map(t => <option key={t} value={t}>{GLOBAL_RELATION_TYPE_TITLES[t]}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 self-end pb-3 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.mandatory} onChange={e => setForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded w-4 h-4" />
            Required
          </label>
          <div className="sm:col-span-2 flex justify-end">
            <Button icon={Plus} onClick={() => add.mutate()} disabled={!form.label.trim()} loading={add.isPending}>Add global relation</Button>
          </div>
        </div>
        {error && <p className="text-sm text-error-500">{error}</p>}
        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Close</Button></div>
      </div>
    </Modal>
  )
}
